import { Component, HostListener, OnInit, ViewChild, ViewContainerRef } from '@angular/core'
import { CtrlBaseComponent } from '../ctrl-base.component';
import { HeaderType } from '../../enums/headerType';
import { breakpoints } from '../../environments/environment.breakpoints';
import { PfComponents } from '../../enums/pfComponents';
import { ModalButtonColor, ModalIconType, ModalType } from '../../enums/modal';
import { Action, ActionType } from '../../models';
import { Observable, Subject } from 'rxjs';

@Component({
  selector: 'ct-header',
  templateUrl: './ct-header.component.html',
  styleUrls: ['./ct-header.component.css'],
  host: {
    '[id]': 'getId()',
    '[hidden]': 'getHidden()',
  }
})
export class CtHeaderComponent extends CtrlBaseComponent implements OnInit{
  
  @ViewChild('target', { read: ViewContainerRef }) target: ViewContainerRef;
  @ViewChild('dialogTarget', { read: ViewContainerRef }) dialogTarget: ViewContainerRef;

  //properties
  private title: String;
  private type: HeaderType;
  private description : String;
  private isFullScreen : string;

  //util
  public isMedium: boolean = false;
  public isSmall: boolean = false;
  public isExtraSmall: boolean = false;
  public iconWidth : number = 131;
  public iconHeight : number = 66;
  private dialogMetadata: any;

  //contants
  public ssvmnLogoPath : string = 'assets/imgs/main-logo.svg';
  public exitIcon : string = 'assets/icons/exit.svg';
  public exitIconMain : string = 'assets/icons/main-exit.svg';

  //rxjs
  private _isFullCloseButtonClickedSubject: Subject<any> = new Subject<any>();
  public isFullCloseButtonClicked$: Observable<any> = this._isFullCloseButtonClickedSubject.asObservable();

  //functions
  @HostListener('window:resize', ['$event'])
  onResize() {
    this.checkScreenSize();
  }

  constructor() {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.checkScreenSize();
  }

  ngAfterViewInit() {
    this.setDialogMetadata();
    if(this.getMetaData().models.length === 0) return;
    this.paintChildren(this.getMetaData(), this.target);
  }

  private setDialogMetadata(): void {
    if(this.type === HeaderType.summary || this.type === HeaderType.text || !this.type) return;
    this.getDialogMetadata();
  }

  /**
   * This function sets the metadata for a dialog component that is gonna be displayed when the user 
   * clicks the exit button.
   */
  private getDialogMetadata(): void {
    this.dialogMetadata = {
      ctType: PfComponents.CtDialog,
      controlProperties: {  
        hidden : false,
        actionButtonText: 'Salir',
        autoOpen: true,
        canBeClosed: true,
        width: 320,
        description: this.description || 'Al salir del proceso perderá los cambios realizados',
        title: '¿Desea salir del proceso?',
        modalType: ModalType.action,
        modalIconType: ModalIconType.leave,
        modalButtonColor: ModalButtonColor.danger,
        id: `${this.getViewId()}_${PfComponents.CtDialog}_default_header_${0}`,
      },
      controlActions: [this.getGoBackAction(`${this.getViewId()}_${PfComponents.CtDialog}_default_header_${0}`)],
      models: []
    };
  }

  /**
  * The function returns an Action object with a specific onClick event and action parameters for
  * navigating back to a previous view.
  */
  private getGoBackAction(id : string): Action {
    let action = new Action('onClick','utl_back',ActionType.VIEW);
    action.actionParams = [id];
    return action;
  }

  /**
   * This function checks the screen size and sets boolean variables based on the breakpoints defined.
   */
  private checkScreenSize() : void {
    const screenWidth = window.innerWidth;
    this.isMedium = screenWidth >= breakpoints.L;
    this.isSmall = screenWidth < breakpoints.L && screenWidth > breakpoints.M;
    this.isExtraSmall = screenWidth <= breakpoints.M;
  }


  /**
   * The function opens a dialog by painting its metadata onto a target element. the dialog
   * will return the user to the previous view when the user clicks the exit button.
   */
  public openDialog(): void {
    this.paintChildren({models: [this.dialogMetadata]}, this.dialogTarget);
    this._isFullCloseButtonClickedSubject.next();
  }

  /**
   * This function returns the path of an exit icon based on the type and size of the header.
   */
  public getExitIconPath(): string {
    if(this.type === HeaderType.white && (this.isSmall || this.isExtraSmall)) {
      return this.exitIconMain;
    }
    return this.exitIcon;
  }

  public get headerType(): typeof HeaderType {
    return HeaderType; 
  }

  /* These are functions that determine whether certain elements of the header component should be
  displayed based on the type of header and the screen size. */
  public shouldShowRegularHeader(): boolean {
    return this.getType() === this.headerType.text || 
           this.getType() === this.headerType.summary || 
           this.getType() === this.headerType.grey ||
           !this.getType();
  }  

  public shouldShowFullHeader(): boolean {
    return this.getType() === this.headerType.primary || 
           this.getType() === this.headerType.white;
  }

  public shouldShowLogo(): boolean {
    return this.isMedium || 
           (this.getType() === this.headerType.primary && this.isSmall);
  }

  public shouldShowTitle(): boolean {
    return (this.getType() === this.headerType.primary && this.isMedium) || 
           this.getType() === this.headerType.white;
  }

  public shouldShowExitText(): boolean {
    return !(this.getType() === this.headerType.white && 
             (this.isSmall || this.isExtraSmall));
  }

  /* These are functions that return boolean values based on the type and size of the header. 
  * In order to place classes on the html elements of the header component.
  */
  public isMediumOrPrimary(): boolean {
    return this.isMedium || this.getType() === this.headerType.primary;
  }

  public isWhite(): boolean {
    return this.getType() === this.headerType.white;
  }  

  public isWhiteAndExtraSmall(): boolean {
    return (this.getType() === this.headerType.white && this.isExtraSmall);
  }
  
  getTitle() { return this.title }
  getType() { return this.type }
  getDescription() { return this.description }
  getIsFullScreen() { return this.isFullScreen }

  setTitle(title: String) { this.title = title }
  setType(type: HeaderType) { this.type = type }
  setDescription(description: String) { this.description = description }
  setIsFullScreen(isFullScreen: string) { this.isFullScreen = isFullScreen }
}